/**
 * تجميعات مبيانات صفحات جلسات/إرسالات الطالب (نفس نطاق التصدير CSV).
 */

function addToMap(map, rawKey) {
  const label = rawKey == null || String(rawKey).trim() === '' ? '—' : String(rawKey).trim();
  map.set(label, (map.get(label) || 0) + 1);
}

function topNFromMap(map, n = 12) {
  return [...map.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}

function sortSecondaryByCountDesc(arr) {
  return [...arr].sort((a, b) => b.count - a.count);
}

function scoreBucketKeyFromPct(pct) {
  const p = Number(pct);
  if (Number.isNaN(p) || pct == null || pct === '') return '?';
  if (p < 25) return '0-25';
  if (p < 50) return '25-50';
  if (p < 75) return '50-75';
  return '75-100';
}

function bucketArrayFromCounts(buckets) {
  const order = ['0-25', '25-50', '50-75', '75-100', '?'];
  return order.map((key) => ({ key, count: buckets[key] || 0 }));
}

function timelineFromRows(rows, dateIsoResolver) {
  const dayMap = new Map();
  for (const row of rows || []) {
    const iso = dateIsoResolver(row);
    if (!iso) continue;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) continue;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const key = `${y}-${m}-${day}`;
    dayMap.set(key, (dayMap.get(key) || 0) + 1);
  }
  return [...dayMap.entries()]
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function exercisePrimaryLabel(row) {
  const name = (row.exercise_name || row.exercise_title || row.exerciseName || '').trim();
  const id = row.exercise_id != null ? String(row.exercise_id) : '';
  if (name) return name.length > 56 ? `${name.slice(0, 53)}…` : name;
  if (id) return id;
  return '—';
}

function gamePrimaryLabel(row) {
  const name =
    row.game_name ||
    row.game_title ||
    row.game_name_en ||
    row.game_name_ar ||
    row.gameName ||
    '';
  const trimmed = name ? String(name).trim() : '';
  const id = row.game_id != null ? row.game_id : row.gameId != null ? row.gameId : '';
  if (trimmed) return trimmed.length > 56 ? `${trimmed.slice(0, 53)}…` : trimmed;
  if (id !== '' && id != null) return String(id);
  return '—';
}

export function aggregateDailyExercisesStudentSessions(rows) {
  const primaryMap = new Map();
  const bandMap = new Map();
  const buckets = { '0-25': 0, '25-50': 0, '50-75': 0, '75-100': 0, '?': 0 };

  for (const row of rows || []) {
    addToMap(primaryMap, exercisePrimaryLabel(row));
    const bandRaw =
      row.success_band != null && row.success_band !== '' ? String(row.success_band).trim() : '—';
    addToMap(bandMap, bandRaw);
    const bk = scoreBucketKeyFromPct(row.session_score_pct);
    buckets[bk] += 1;
  }

  return {
    primary: topNFromMap(primaryMap),
    secondary: sortSecondaryByCountDesc([...bandMap.entries()].map(([name, count]) => ({ name, count }))),
    tertiary: { type: 'score', rows: bucketArrayFromCounts(buckets) },
    timeline: timelineFromRows(rows, (r) => r.started_at || r.completed_at)
  };
}

export function aggregateFlashAnzanStudentSessions(rows) {
  const primaryMap = new Map();
  const bandMap = new Map();
  const buckets = { '0-25': 0, '25-50': 0, '50-75': 0, '75-100': 0, '?': 0 };

  for (const row of rows || []) {
    const op = (row.operation_type || row.operationType || '').trim() || '—';
    addToMap(primaryMap, op);
    const bandRaw =
      row.success_band != null && row.success_band !== '' ? String(row.success_band).trim() : '—';
    addToMap(bandMap, bandRaw);
    const bk = scoreBucketKeyFromPct(row.session_score_pct);
    buckets[bk] += 1;
  }

  return {
    primary: topNFromMap(primaryMap),
    secondary: sortSecondaryByCountDesc([...bandMap.entries()].map(([name, count]) => ({ name, count }))),
    tertiary: { type: 'score', rows: bucketArrayFromCounts(buckets) },
    timeline: timelineFromRows(rows, (r) => r.started_at || r.completed_at)
  };
}

export function aggregateFlashCardsStudentSessions(rows) {
  const primaryMap = new Map();
  const bandMap = new Map();
  const buckets = { '0-25': 0, '25-50': 0, '50-75': 0, '75-100': 0, '?': 0 };

  for (const row of rows || []) {
    const st = (row.status || row.session_status || '').trim() || '—';
    addToMap(primaryMap, st);
    const bandRaw =
      row.success_band != null && row.success_band !== '' ? String(row.success_band).trim() : '—';
    addToMap(bandMap, bandRaw);
    const bk = scoreBucketKeyFromPct(row.session_score_pct);
    buckets[bk] += 1;
  }

  return {
    primary: topNFromMap(primaryMap),
    secondary: sortSecondaryByCountDesc([...bandMap.entries()].map(([name, count]) => ({ name, count }))),
    tertiary: { type: 'score', rows: bucketArrayFromCounts(buckets) },
    timeline: timelineFromRows(rows, (r) => r.started_at || r.completed_at)
  };
}

export function aggregateGamesStudentSessions(rows) {
  const primaryMap = new Map();
  const bandMap = new Map();
  const buckets = { '0-25': 0, '25-50': 0, '50-75': 0, '75-100': 0, '?': 0 };

  for (const row of rows || []) {
    addToMap(primaryMap, gamePrimaryLabel(row));
    const bandRaw =
      row.success_band != null && row.success_band !== '' ? String(row.success_band).trim() : '—';
    addToMap(bandMap, bandRaw);
    const bk = scoreBucketKeyFromPct(row.session_score_pct);
    buckets[bk] += 1;
  }

  return {
    primary: topNFromMap(primaryMap),
    secondary: sortSecondaryByCountDesc([...bandMap.entries()].map(([name, count]) => ({ name, count }))),
    tertiary: { type: 'score', rows: bucketArrayFromCounts(buckets) },
    timeline: timelineFromRows(rows, (r) => r.started_at || r.completed_at)
  };
}

function normalizeResult01(row) {
  const v = row.result_correct ?? row.resultCorrect;
  if (v === true || v === 1 || v === '1') return '1';
  if (v === false || v === 0 || v === '0') return '0';
  if (v == null || v === '') return '?';
  const n = Number(v);
  if (n === 1) return '1';
  if (n === 0) return '0';
  return '?';
}

function challengeNameLabel(row) {
  const n =
    row.challenge_name ||
    row.challenge_title ||
    row.title ||
    row.name ||
    row.challengeName ||
    '';
  const trimmed = n ? String(n).trim() : '';
  const id = row.challenge_id != null ? row.challenge_id : row.challengeId != null ? row.challengeId : '';
  if (trimmed) return trimmed.length > 56 ? `${trimmed.slice(0, 53)}…` : trimmed;
  if (id !== '' && id != null) return String(id);
  return '—';
}

function challengeStatusRaw(row) {
  const s = row.challenge_status ?? row.status;
  return s != null && s !== '' ? String(s).trim() : '—';
}

export function aggregateChallengesStudentSubmissions(rows) {
  const primaryMap = new Map();
  const statusMap = new Map();
  const resultCounts = { '1': 0, '0': 0, '?': 0 };

  for (const row of rows || []) {
    addToMap(primaryMap, challengeNameLabel(row));
    addToMap(statusMap, challengeStatusRaw(row));
    const rk = normalizeResult01(row);
    resultCounts[rk] += 1;
  }

  const tertiaryRows = [
    { key: '1', count: resultCounts['1'] },
    { key: '0', count: resultCounts['0'] },
    { key: '?', count: resultCounts['?'] }
  ];

  return {
    primary: topNFromMap(primaryMap),
    secondary: sortSecondaryByCountDesc([...statusMap.entries()].map(([name, count]) => ({ name, count }))),
    tertiary: { type: 'result', rows: tertiaryRows },
    timeline: timelineFromRows(rows, (r) => r.submitted_at || r.submittedAt)
  };
}

const AGGREGATORS = {
  dailyExercises: aggregateDailyExercisesStudentSessions,
  flashAnzan: aggregateFlashAnzanStudentSessions,
  flashCards: aggregateFlashCardsStudentSessions,
  games: aggregateGamesStudentSessions,
  challenges: aggregateChallengesStudentSubmissions
};

export function aggregateStudentSessionsForPreset(preset, rows) {
  const fn = AGGREGATORS[preset];
  if (!fn) return aggregateDailyExercisesStudentSessions(rows);
  return fn(rows);
}
